import { createSelector } from 'reselect';
import { get } from 'lodash';
import { ACTIVE_METRICS, METRICS_DEFAULT_ORDER } from '@utils/metrics';
import featureNames from '@utils/featureToggle/constants';
import {
  filterSelector,
  intervalSelector,
  supportedMetricsSelector,
  featureSelector,
  filterColorSelector,
  realTimeModeOnTrendsSelector,
  currentMetricsMetaSelector,
} from '@store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { lineChartReqBody } from '@src/components/metrics-panel-hz/utils/metricCardsReqBodyTemplate';
import { ERROR_METRIC_MAP } from '@utils/dimension/errorCodeHelper';
import { SCOPE } from './types';
import { createJsonTransformOptions } from './query';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

const defaultMetric = Object.keys(ACTIVE_METRICS[CURRENT_PRODUCT_MODE])[0];

const selector = (state) => state[SCOPE];
const minuteGranularityEnabled = featureSelector(featureNames.MINUTE_GRANULARITY);

export const orderedMetricsSelector: any = createSelector(
  currentMetricsMetaSelector,
  supportedMetricsSelector,
  (currentMetricsMeta, supportedMetrics: any) => {
    const defaultOrder = METRICS_DEFAULT_ORDER[CURRENT_PRODUCT_MODE];
    const currentDefaultOrder = Object.keys(currentMetricsMeta).map((metric) => {
      if (!defaultOrder.includes(metric)) {
        return metric;
      }
    });
    const validMetrics = currentDefaultOrder.filter((metric: any) => metric in supportedMetrics);
    return validMetrics;
  },
);

export const metricSelector = createSelector(selector, (state) => state.metrics);

export const liveDateSelector = createSelector(selector, (state) => state.liveDate);

// selector to match dimension header format
export const metricObjectSelector = createSelector(
  supportedMetricsSelector,
  metricSelector,
  (customerMetrics: any, selectedMetrics) => {
    const filteredMetrics = {};
    selectedMetrics.forEach((m) => {
      if (m in customerMetrics) {
        filteredMetrics[m] = customerMetrics[m];
      }
    });
    return filteredMetrics;
  },
);

export const querySelector = createSelector(
  filterSelector,
  intervalSelector,
  metricSelector,
  filterColorSelector,
  minuteGranularityEnabled,
  realTimeModeOnTrendsSelector,
  currentMetricsMetaSelector,
  (
    filter,
    interval,
    metrics,
    filterColor,
    minuteGranularity,
    realTimeOnTrends,
    currentMetricsMeta,
  ) => {
    // state keeps count metrics, add pct metrics if available
    const queryMetrics = [...metrics];
    queryMetrics.forEach((m) => {
      /** duplicate metrics may exist in queryMetrics after this operation */
      const percentageMetric = getPercentageMetricByKey({ metricKey: m });
      if (percentageMetric) {
        queryMetrics.push(percentageMetric);
      }
    });

    const baseQuery = JSON.parse(JSON.stringify(lineChartReqBody));
    baseQuery.queries[0].filter = filter;
    baseQuery.queries[0].interval = `${interval.from}/${interval.to}`;
    baseQuery.queries[0].granularity = interval.granularity; // granularity(interval.from, interval.to, minuteGranularity);
    /** remove duplicate metrics before send to server */
    baseQuery.queries[0].metrics = Array.from(new Set(queryMetrics)).sort();
    baseQuery.queries[0].isLiveMode = realTimeOnTrends;

    return {
      metrics: queryMetrics,
      reqBody: baseQuery,
      transformOptions: createJsonTransformOptions(queryMetrics, filterColor, currentMetricsMeta),
    };
  },
);

export const exportQuerySelector = createSelector(
  querySelector,
  orderedMetricsSelector,
  (base, metrics) => {
    const query = JSON.parse(JSON.stringify(get(base, 'reqBody.queries.0', {})));
    query.metrics = metrics.map((m) => getPercentageMetricByKey({ metricKey: m, defaultValue: m }));
    return query;
  },
);

export const chartDataSelector = createSelector(selector, (state) => {
  const { chartData } = state;
  return chartData;
});

export const exportChartDataSelector = createSelector(selector, (state) => {
  const { chartData } = state;
  const exportData = {};
  Object.keys(chartData).map((metric) => {
    const metricTotalsCol = ['Total', get(chartData, [metric, '_aggregate'])];
    const timeSeriesData = get(chartData, [metric, 'series', 0, 'data'], []);
    exportData[metric] = [...timeSeriesData, metricTotalsCol];
  });
  return exportData;
});

export const loadingSelector = createSelector(selector, (state) => ({
  loading: state.loading,
  showError: state.showError,
}));

const exportedSelector = (state, arg) =>
  state[SCOPE].query.queries[0].metrics.filter((metric) => metric === arg);

export default selector;

export const exportedMetricSelector = createSelector(
  exportedSelector,
  (exportedMetric) => exportedMetric,
);

export const selectedMetricSelector = createSelector(
  selector,
  (store) => store.metric || defaultMetric,
);

export const isErrorMetricSelectedSelector = createSelector(
  selectedMetricSelector,
  (metric) => metric in ERROR_METRIC_MAP,
);
